import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import { isSheetOwner } from '@roll1/shared/sheet-permissions.js';
import { useSessionContext } from './SessionContext';
import { useSheetContext } from './SheetContext';
import { useInitiativeContext } from './InitiativeContext';
import { useTokenContext } from './TokenContext';
import { useSelectionContext } from './SelectionContext';

/**
 * ActorContext — «за кого я действую».
 *
 * Актор — это лист персонажа, от лица которого участник совершает действия
 * (броски, атаки, касты) и который показывает плавающий Character HUD.
 * Разведён с canvas-selection намеренно: клик по токену — это «что я трогаю
 * на карте», а не «кем я играю», поэтому HUD больше не скачет за выделением.
 *
 * Правила:
 * - игрок может быть актором только своих листов (isSheetOwner — тот же
 *   предикат, что на сервере в roll:request / canModifySheet);
 * - GM — любого листа;
 * - у игрока актор выводится автоматически (первый собственный лист),
 *   ручной выбор (например, при двух персонажах) перекрывает автовывод;
 * - у GM актор автоследует за ходом инициативы (ход NPC → актор = его лист)
 *   и переключается вручную (ПКМ по токену → «Действовать за»).
 *
 * Ручной выбор персистится per-campaign в localStorage.
 */

export interface ActorContextValue {
  /** Лист, за который действует текущий участник (null — актора нет). */
  actorSheetId: string | null;
  /** Ручное переключение актора. Молча игнорирует листы, за которые участник действовать не может. */
  setActor: (sheetId: string | null) => void;
  /** Может ли текущий участник действовать за этот лист (клиентское зеркало серверного предиката). */
  canActAs: (sheetId: string) => boolean;
}

const ActorContext = createContext<ActorContextValue | null>(null);

const storageKey = (campaignId: string) => `roll1:actor:${campaignId}`;

export function ActorProvider({ children }: { children: ReactNode }) {
  const { mp, isGM, pages } = useSessionContext();
  const { sheets: charSheets } = useSheetContext();
  const { initiative } = useInitiativeContext();
  const { tokens: tokensHook } = useTokenContext();

  const campaignId: string | null = mp.session?.id ?? null;
  const participantName = mp.participant?.name ?? '';
  const participantUserId = mp.participant?.userId ?? undefined;

  const [manualActorId, setManualActorId] = useState<string | null>(() => {
    if (!campaignId || typeof window === 'undefined') return null;
    try {
      return localStorage.getItem(storageKey(campaignId));
    } catch {
      return null;
    }
  });

  const canActAs = useCallback((sheetId: string): boolean => {
    if (isGM) return true;
    const sheet = charSheets.sheets.find((s: { id: string }) => s.id === sheetId);
    return !!sheet && isSheetOwner(sheet, participantName, participantUserId);
  }, [isGM, charSheets.sheets, participantName, participantUserId]);

  const persistActor = useCallback((sheetId: string | null) => {
    if (!campaignId || typeof window === 'undefined') return;
    try {
      if (sheetId) localStorage.setItem(storageKey(campaignId), sheetId);
      else localStorage.removeItem(storageKey(campaignId));
    } catch { /* localStorage unavailable — degrade to session-only */ }
  }, [campaignId]);

  const setActor = useCallback((sheetId: string | null) => {
    if (sheetId !== null && !canActAs(sheetId)) return;
    setManualActorId(sheetId);
    persistActor(sheetId);
  }, [canActAs, persistActor]);

  // GM: актор автоследует за ходом инициативы. Токен текущей строки может
  // жить не на активной странице — ищем сначала в живом TokenContext,
  // затем по данным остальных страниц. Токен без листа не сбрасывает
  // актора (GM продолжает действовать за предыдущего).
  useEffect(() => {
    if (!isGM) return;
    const entry = initiative.entries[initiative.currentIndex];
    if (!entry) return;
    // Живые токены активной страницы и сериализованные с остальных страниц —
    // разные типы; для актора нужен только characterSheetId.
    let token: { characterSheetId?: string | null } | undefined =
      tokensHook.tokens.find((t: { id: string }) => t.id === entry.tokenId);
    if (!token) {
      for (const p of pages.pages) {
        const pd = pages.getPageData(p.id);
        const found = pd?.tokens?.find((t: { id: string }) => t.id === entry.tokenId);
        if (found) { token = found; break; }
      }
    }
    const sheetId = token?.characterSheetId;
    if (sheetId && charSheets.sheets.some((s: { id: string }) => s.id === sheetId)) {
      setManualActorId(sheetId);
      persistActor(sheetId);
    }
    // Намеренно только по смене хода — не при каждом изменении листов/токенов.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGM, initiative.currentIndex, initiative.entries]);

  // Выделение СВОЕГО токена на карте = «действую за него» — стандартная
  // VTT-привычка (Foundry), которую отвязка HUD от selection не должна
  // ломать. Переключаем актора только когда выделен ОДИН токен с листом,
  // за который участник может действовать; чужие токены, мульти-выделение
  // и деселект актора не трогают (HUD не «пропадает» от клика в пустоту).
  const { selection } = useSelectionContext();
  useEffect(() => {
    if (selection.type !== 'token') return;
    const token = tokensHook.tokens.find((t: { id: string }) => t.id === selection.tokenId);
    const sheetId = (token as { characterSheetId?: string | null } | undefined)?.characterSheetId;
    if (!sheetId || !charSheets.sheets.some((s: { id: string }) => s.id === sheetId)) return;
    if (!canActAs(sheetId)) return;
    setManualActorId(sheetId);
    persistActor(sheetId);
    // Только по смене выделения — не по каждому обновлению листов/токенов.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  // Эффективный актор: валидный ручной выбор → автовывод (свой лист) → null.
  const actorSheetId = useMemo<string | null>(() => {
    if (
      manualActorId &&
      charSheets.sheets.some((s: { id: string }) => s.id === manualActorId) &&
      canActAs(manualActorId)
    ) {
      return manualActorId;
    }
    if (!isGM) {
      const own = charSheets.sheets.find(
        (s) => isSheetOwner(s, participantName, participantUserId),
      );
      return own?.id ?? null;
    }
    return null;
  }, [manualActorId, charSheets.sheets, canActAs, isGM, participantName, participantUserId]);

  const value = useMemo<ActorContextValue>(
    () => ({ actorSheetId, setActor, canActAs }),
    [actorSheetId, setActor, canActAs],
  );

  return <ActorContext.Provider value={value}>{children}</ActorContext.Provider>;
}

export function useActorContext(): ActorContextValue {
  const ctx = useContext(ActorContext);
  if (!ctx) throw new Error('useActorContext must be used within ActorProvider');
  return ctx;
}
